import NetInfo from '@react-native-community/netinfo';
import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';
import { Platform } from 'react-native';
import { API_BASE_URL, FALLBACK_API_URL, PROD_API_URL } from '../../config/api';
import { notifySessionInvalidated } from '../../utils/authEvents';
import logger from '../../utils/logger';
import { clearAllUserData, platformStorage } from '../../utils/storage';

// 🔄 Fallback state management
let currentBaseURL = API_BASE_URL;
let usedFallback = false;

// ⚠️ PERMANENT SESSION MODE - NEVER EXPIRES
// Token lasts 30 days, Refresh token lasts 365 days
// No auto-refresh - only refreshes on 401 errors
// User stays logged in until manual logout or app uninstall

// 🔒 Refresh Mutex - Prevents multiple simultaneous refresh attempts
let isRefreshing = false;
let refreshSubscribers: ((token: string) => void)[] = [];
let refreshRejectors: ((error: any) => void)[] = [];

// Safety timeout: if refresh takes more than 30s, unlock the mutex
let refreshTimeoutHandle: ReturnType<typeof setTimeout> | null = null;

const clearRefreshTimeout = () => {
  if (refreshTimeoutHandle) {
    clearTimeout(refreshTimeoutHandle);
    refreshTimeoutHandle = null;
  }
};

const onRefreshed = (token: string) => {
  clearRefreshTimeout();
  refreshSubscribers.forEach((callback) => callback(token));
  refreshSubscribers = [];
  refreshRejectors = [];
};

const onRefreshFailed = (error: any) => {
  clearRefreshTimeout();
  refreshRejectors.forEach((callback) => callback(error));
  refreshSubscribers = [];
  refreshRejectors = [];
};

const addRefreshSubscriber = (onSuccess: (token: string) => void, onFail: (error: any) => void) => {
  refreshSubscribers.push(onSuccess);
  refreshRejectors.push(onFail);
};

// 🌐 Network recovery: reset fallback + unlock stuck mutex when connection returns
NetInfo.addEventListener((state) => {
  if (state.isConnected) {
    if (usedFallback) {
      logger.log('🌐 [NET] Network recovered - resetting to primary API URL');
      usedFallback = false;
      currentBaseURL = API_BASE_URL;
      api.defaults.baseURL = API_BASE_URL;
    }
    // If mutex was stuck (e.g. refresh call lost mid-flight), unlock it
    if (isRefreshing) {
      logger.warn('🌐 [NET] Network recovered but isRefreshing=true - unlocking mutex');
      isRefreshing = false;
      onRefreshFailed(new Error('Network changed during token refresh'));
    }
  }
});

// Export api instance for direct use
export const api = axios.create({
  baseURL: currentBaseURL,
  timeout: __DEV__ ? 30000 : 10000, // 10s production (mobile networks), 30s dev
  headers: {
    'Content-Type': 'application/json',
  },
  // Use default axios validateStatus (2xx = success, 4xx/5xx = error)
  // This ensures 401 responses properly trigger the error interceptor for token refresh
});

// Log API configuration on startup
logger.log('🌐 API Configuration:', {
  baseURL: API_BASE_URL,
  platform: Platform.OS,
  isDev: __DEV__,
});

// Request interceptor to add auth token
api.interceptors.request.use(
  async (config: InternalAxiosRequestConfig) => {
    const token = await platformStorage.getItem('access_token');
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
      logger.debug('🔐 Added auth token to request');
    }

    logger.log('📡 API REQUEST:', {
      method: config.method?.toUpperCase(),
      url: config.url,
      hasToken: !!token,
      ...(__DEV__ && {
        baseURL: config.baseURL,
        fullURL: `${config.baseURL}${config.url}`,
        params: config.params,
      }),
    });

    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

// 🔄 Fallback mechanism - Switch to backup URL on connection error
// ⚠️ DISABLED in production: FALLBACK_API_URL is a local IP (unreachable from mobile networks/5G)
const shouldUseFallback = (error: AxiosError): boolean => {
  // Never switch to local fallback when using the production HTTPS URL —
  // the fallback IP (72.60.214.199) is only reachable on the same LAN.
  if (API_BASE_URL === PROD_API_URL) return false;

  return (
    !error.response &&
    (error.code === 'ECONNABORTED' ||
      error.code === 'ENOTFOUND' ||
      error.code === 'ECONNREFUSED' ||
      error.message.includes('Network Error') ||
      error.message.includes('timeout'))
  );
};

const isRefreshSessionFailure = (status?: number) =>
  status === 400 || status === 401 || status === 403 || status === 429;

const invalidateSession = async (
  reason: 'missing_refresh_token' | 'refresh_rejected' | 'rate_limited',
) => {
  logger.warn('🔒 [AUTH] Invalidating local session:', reason);
  usedFallback = false;
  currentBaseURL = API_BASE_URL;
  api.defaults.baseURL = API_BASE_URL;
  await clearAllUserData();
  notifySessionInvalidated(reason);
};

// Response interceptor to handle token refresh with mutex
api.interceptors.response.use(
  (response) => {
    if (__DEV__) {
      logger.log('✅ API RESPONSE:', {
        method: response.config.method?.toUpperCase(),
        url: response.config.url,
        status: response.status,
        dataSize: JSON.stringify(response.data).length,
      });
    }
    return response;
  },
  async (error: AxiosError) => {
    // 🔄 Fallback logic
    if (!usedFallback && shouldUseFallback(error) && FALLBACK_API_URL) {
      logger.warn('🔄 Primary API failed, switching to fallback URL');
      usedFallback = true;
      currentBaseURL = FALLBACK_API_URL;
      api.defaults.baseURL = FALLBACK_API_URL;

      const config = error.config;
      if (config) {
        return api.request(config);
      }
    }

    logger.error('❌ API ERROR:', {
      method: error.config?.method?.toUpperCase(),
      url: error.config?.url,
      message: error.message,
      code: error.code,
      status: error.response?.status,
      ...(__DEV__ && {
        baseURL: error.config?.baseURL,
        fullURL: `${error.config?.baseURL}${error.config?.url}`,
        timeout: error.config?.timeout,
      }),
    });

    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

    // If 401 and not a retry, try to refresh token
    if (error.response?.status === 401 && !originalRequest._retry) {
      if (isRefreshing) {
        logger.log('🔄 Token refresh already in progress, waiting...');
        return new Promise((resolve, reject) => {
          addRefreshSubscriber(
            (token: string) => {
              if (originalRequest.headers) {
                originalRequest.headers.Authorization = `Bearer ${token}`;
              }
              resolve(api(originalRequest));
            },
            (err: any) => reject(err),
          );
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      // Safety timeout: unlock mutex after 30s to prevent permanent lock
      refreshTimeoutHandle = setTimeout(() => {
        if (isRefreshing) {
          logger.error('⏰ [AUTH] Token refresh timed out after 30s - unlocking mutex');
          isRefreshing = false;
          onRefreshFailed(new Error('Token refresh timeout'));
        }
      }, 30_000);

      try {
        const refreshToken = await platformStorage.getItem('refresh_token');
        if (!refreshToken) {
          logger.warn('⚠️ [AUTH] No refresh token available - ending local session');
          isRefreshing = false;
          clearRefreshTimeout();
          onRefreshFailed(error);
          await invalidateSession('missing_refresh_token');
          return Promise.reject(error);
        }

        logger.log('🔄 [AUTH] Starting token refresh (mutex locked)...');
        const startTime = Date.now();

        const response = await axios.post(`${API_BASE_URL}/auth/refresh`, {
          refreshToken: refreshToken,
        });

        const { access, refresh: newRefreshToken } = response.data.tokens;

        if (!access || typeof access !== 'string') {
          throw new Error('Invalid refresh response: missing access token');
        }

        await Promise.all([
          platformStorage.setItem('access_token', access),
          ...(newRefreshToken ? [platformStorage.setItem('refresh_token', newRefreshToken)] : []),
        ]);

        const duration = Date.now() - startTime;
        logger.log(`✅ [AUTH] Token refreshed successfully in ${duration}ms`);

        onRefreshed(access);
        isRefreshing = false;

        if (originalRequest.headers) {
          originalRequest.headers.Authorization = `Bearer ${access}`;
        }
        return api(originalRequest);
      } catch (refreshError: any) {
        isRefreshing = false;

        const refreshStatus = refreshError.response?.status;
        const errorDetails = {
          message: refreshError.message,
          code: refreshError.code,
          status: refreshStatus,
          data: refreshError.response?.data,
        };
        logger.error('❌ [AUTH] Token refresh failed:', errorDetails);

        onRefreshFailed(refreshError);

        if (isRefreshSessionFailure(refreshStatus)) {
          await invalidateSession(refreshStatus === 429 ? 'rate_limited' : 'refresh_rejected');
        }

        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  },
);

export default api;
